import React, { memo, useMemo, useState } from 'react'
import { Form, Typography } from 'antd'
import { isEmpty, size } from 'lodash'
import PropTypes from 'prop-types'

import { antFormItemNamePropType } from '../../prop-types'
import {
  formatFormItemLabel,
  formatFormListLabel,
  getFormItemLabelAboveConfiguration,
} from '../../utils'

import LazyTreeSelect from './lazy-tree-select'

const FormLazyTreeSelect = ({
  form,
  name,
  label,
  treeData,
  rules,
  placeholder,
  initialValue,
  groupedFlatTreeDataByValue,
  flatTreeData,
  showColon,
  labelAbove,
  treeCheckable,
  noStyle,
  ...rest
}) => {
  const [listSize, setListSize] = useState(
    treeCheckable ? size(form.getFieldValue(name)) : 0,
  )

  const labelContent = useMemo(() => {
    if (isEmpty(label)) {
      return ''
    }

    const formListLabel = formatFormListLabel(label, listSize, treeCheckable)
    return formatFormItemLabel(formListLabel, showColon)
  }, [label, listSize, treeCheckable, showColon])

  return (
    <Form.Item
      labelCol={getFormItemLabelAboveConfiguration(labelAbove)}
      name={name}
      label={<Typography.Text>{labelContent}</Typography.Text>}
      rules={rules}
      colon={showColon}
      noStyle={noStyle}
    >
      <LazyTreeSelect
        name={name}
        treeData={treeData}
        treeCheckable={treeCheckable}
        setListSize={setListSize}
        flatTreeData={flatTreeData}
        groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
        placeholder={placeholder}
        initialValue={initialValue}
        {...rest}
      />
    </Form.Item>
  )
}

FormLazyTreeSelect.propTypes = {
  name: antFormItemNamePropType.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  treeData: PropTypes.array.isRequired,
  rules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  form: PropTypes.object.isRequired,
  groupedFlatTreeDataByValue: PropTypes.object,
  flatTreeData: PropTypes.arrayOf(PropTypes.object),
  initialValue: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        title: PropTypes.string,
      }),
    ),
    PropTypes.string.isRequired,
  ]),
  showColon: PropTypes.bool,
  labelAbove: PropTypes.bool,
  treeCheckable: PropTypes.bool,
  noStyle: PropTypes.bool,
}

FormLazyTreeSelect.defaultProps = {
  label: '',
  treeCheckable: false,
  placeholder: 'Select an item...',
  rules: undefined,
  groupedFlatTreeDataByValue: {},
  flatTreeData: [],
  initialValue: [],
  labelAbove: false,
  showColon: false,
  noStyle: false,
}

export default memo(FormLazyTreeSelect)
